/**
 * postMessage protocol between the host app (CommunityOS resident-app /
 * signage-player, or the Rendever tablet app) and the games iframe.
 *
 * - All messages carry a `protocol` discriminator (`'rendever-games'`) so we
 *   don't collide with other postMessage traffic.
 * - All messages carry a `v` major version. Receivers MUST reject messages
 *   whose major doesn't match their own.
 * - All messages carry an `id` (uuid) so request/response pairs can correlate.
 * - The schemas below are Zod schemas + inferred TypeScript types. Consumers
 *   that only need types pay zero runtime cost; consumers that want runtime
 *   validation can import the schemas.
 */
import { z } from 'zod';
import { PROTOCOL_MAJOR } from './version.js';
// ---------------------------------------------------------------------------
// Shared field schemas
// ---------------------------------------------------------------------------
export const ProtocolTag = z.literal('rendever-games');
export const ProtocolMajor = z.literal(PROTOCOL_MAJOR);
export const MessageEnvelope = z.object({
    protocol: ProtocolTag,
    v: ProtocolMajor,
    id: z.string().uuid(),
});
export const Surface = z.enum(['resident-app', 'signage', 'tablet', 'standalone']);
export const QualityTier = z.enum(['high', 'medium', 'low']);
export const InputMode = z.enum(['touch', 'pointer', 'dpad', 'companion']);
export const HapticStyle = z.enum(['light', 'medium', 'heavy', 'success', 'warning', 'error']);
export const AccessibilityProfile = z.object({
    textSize: z.enum(['standard', 'large', 'largest']).default('standard'),
    highContrast: z.boolean().default(false),
    reduceMotion: z.boolean().default(false),
    captionsOn: z.boolean().default(false),
    audioOn: z.boolean().default(true),
});
// ---------------------------------------------------------------------------
// Host → Iframe messages
// ---------------------------------------------------------------------------
/**
 * Sent once by the host as soon as the iframe posts `ready`. Provides the
 * iframe with everything it needs to operate without its own login UI.
 */
export const HostInit = MessageEnvelope.extend({
    type: z.literal('init'),
    payload: z.object({
        firebaseIdToken: z.string().min(1),
        residentId: z.string(),
        buildingId: z.string().optional(),
        locale: z.string().default('en-US'),
        surface: Surface,
        qualityTier: QualityTier.default('high'),
        inputMode: InputMode.default('touch'),
        accessibility: AccessibilityProfile,
        /** Hosts may set this to route telemetry into a demo bucket. */
        isDemo: z.boolean().default(false),
    }),
});
export const HostPause = MessageEnvelope.extend({ type: z.literal('pause') });
export const HostResume = MessageEnvelope.extend({ type: z.literal('resume') });
export const HostQuit = MessageEnvelope.extend({ type: z.literal('quit') });
export const HostConfigUpdate = MessageEnvelope.extend({
    type: z.literal('config_update'),
    payload: z.object({
        accessibility: AccessibilityProfile.partial().optional(),
        qualityTier: QualityTier.optional(),
        inputMode: InputMode.optional(),
    }),
});
export const HostBridgeResponse = MessageEnvelope.extend({
    type: z.literal('bridge_response'),
    /** Matches the `id` of the original request_* message from the iframe. */
    replyTo: z.string().uuid(),
    ok: z.boolean(),
    error: z.string().optional(),
    /** Optional return data for successful requests (e.g. share completed). */
    payload: z.record(z.string(), z.unknown()).optional(),
});
export const HostMessage = z.discriminatedUnion('type', [
    HostInit,
    HostPause,
    HostResume,
    HostQuit,
    HostConfigUpdate,
    HostBridgeResponse,
]);
// ---------------------------------------------------------------------------
// Iframe → Host messages
// ---------------------------------------------------------------------------
export const IframeReady = MessageEnvelope.extend({
    type: z.literal('ready'),
    payload: z.object({
        appVersion: z.string(),
        supportedFeatures: z.array(z.string()).default([]),
    }),
});
export const IframeSessionStarted = MessageEnvelope.extend({
    type: z.literal('session_started'),
    payload: z.object({
        sessionPublicId: z.string().uuid(),
        gameSlug: z.string(),
        difficultyLevel: z.number().int().nonnegative(),
    }),
});
export const IframeSessionEnded = MessageEnvelope.extend({
    type: z.literal('session_ended'),
    payload: z.object({
        sessionPublicId: z.string().uuid(),
        gameSlug: z.string(),
        durationMs: z.number().int().nonnegative(),
        score: z.number().int().nonnegative(),
        accuracy: z.number().min(0).max(1),
        completed: z.boolean(),
    }),
});
export const IframeAchievementUnlocked = MessageEnvelope.extend({
    type: z.literal('achievement_unlocked'),
    payload: z.object({
        slug: z.string(),
        title: z.string(),
    }),
});
/**
 * Iframe asks the host to queue an event for durable offline storage.
 * The host owns the IndexedDB outbox and syncs to Rails when online.
 *
 * The shape mirrors `GameEvent` in `./api.ts` — the host stores these rows
 * verbatim and forwards them as the `events` array in a `SyncRequest`.
 */
export const IframeQueueEvent = MessageEnvelope.extend({
    type: z.literal('queue_event'),
    payload: z.object({
        /** Append-only event row destined for POST /game_sync */
        sessionPublicId: z.string().uuid(),
        seq: z.number().int().nonnegative(),
        occurredAt: z.string().datetime(),
        /** Ms since session start — matches GameEvent.elapsedMs. */
        elapsedMs: z.number().int().nonnegative(),
        eventType: z.string(),
        /** Set on response events (move/correct/incorrect). Null otherwise. */
        reactionTimeMs: z.number().int().nonnegative().nullable().default(null),
        payload: z.record(z.string(), z.unknown()).default({}),
    }),
});
export const IframeRequestHaptic = MessageEnvelope.extend({
    type: z.literal('request_haptic'),
    payload: z.object({ style: HapticStyle }),
});
export const IframeRequestShare = MessageEnvelope.extend({
    type: z.literal('request_share'),
    payload: z.object({
        title: z.string().optional(),
        text: z.string(),
        url: z.string().url().optional(),
    }),
});
export const IframeRequestClose = MessageEnvelope.extend({
    type: z.literal('request_close'),
});
export const IframeFocusOut = MessageEnvelope.extend({
    type: z.literal('focus_out'),
    payload: z.object({
        /** Where the iframe wants focus to go in the host chrome. */
        direction: z.enum(['up', 'down', 'left', 'right', 'back']),
    }),
});
export const IframeAnalyticsEvent = MessageEnvelope.extend({
    type: z.literal('analytics_event'),
    payload: z.object({
        name: z.string(),
        props: z.record(z.string(), z.unknown()).default({}),
    }),
});
/**
 * Iframe reports a fatal error to the host. The host should log this,
 * surface it (a generic "Something went wrong" UI), and may tear down or
 * reload the iframe depending on severity.
 *
 * This is NOT for per-trial gameplay errors — those go through
 * `queue_event` with an appropriate `eventType`. This is for things that
 * break the session: failed asset load, unrecoverable runtime exception,
 * protocol-version mismatch detected at runtime, etc.
 */
export const IframeError = MessageEnvelope.extend({
    type: z.literal('error'),
    payload: z.object({
        /** Stable machine-readable code, e.g. `asset_load_failed`. */
        code: z.string(),
        /** Human-readable message. Safe to log; do NOT show resident-side. */
        message: z.string(),
        severity: z.enum(['warning', 'fatal']).default('fatal'),
        /** Optional session this error is associated with. */
        sessionPublicId: z.string().uuid().optional(),
        /** Free-form context for debugging. */
        context: z.record(z.string(), z.unknown()).default({}),
    }),
});
export const IframeMessage = z.discriminatedUnion('type', [
    IframeReady,
    IframeSessionStarted,
    IframeSessionEnded,
    IframeAchievementUnlocked,
    IframeQueueEvent,
    IframeRequestHaptic,
    IframeRequestShare,
    IframeRequestClose,
    IframeFocusOut,
    IframeAnalyticsEvent,
    IframeError,
]);
// ---------------------------------------------------------------------------
// Union of every message (for receivers that want one parse)
// ---------------------------------------------------------------------------
export const AnyMessage = z.union([HostMessage, IframeMessage]);
//# sourceMappingURL=messages.js.map